import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { outstandingItems, type LoanAgreement } from "@/contracts";

import {
  cancelLoanAgreementMutation,
  closeLoanAgreementMutation,
  issueLoanAgreementMutation,
  loanAgreementQuery,
  renewLoanAgreementMutation,
  serviceAgreementsQuery,
} from "@/api/queries";
import { hasRole, hasView, useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ErrorBlock, LoadingBlock } from "@/components/ui/page";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import {
  ServiceAgreementCreateDialog,
  type ServiceAgreementPrefill,
} from "@/features/services/service-agreement-create-dialog";
import { SERVICE_STATUS_LABELS, SERVICE_STATUS_TONES } from "@/features/services/service-labels";
import { useApiClient } from "@/lib/api-context";
import { EM_DASH, formatDate, formatDateTime, formatDevEui } from "@/lib/format";
import { AttestSignatureDialog } from "./attest-signature-dialog";
import { LoanAgreementCreateDialog } from "./loan-agreement-create-dialog";
import { RecordReturnDialog } from "./record-return-dialog";
import {
  LEGAL_PERSON_LABELS,
  LOAN_OUTCOME_LABELS,
  LOAN_STATUS_LABELS,
  LOAN_STATUS_TONES,
} from "./loan-labels";

/**
 * One loan agreement: the controls, and the instrument itself.
 *
 * Operators can print from the browser or download a server-built PDF of the
 * freeze-at-issue text for the expediente (ADR-076). Both are file copies,
 * not digital signatures. Interface chrome carries `print:hidden`.
 */
export function LoanAgreementPage() {
  const { t } = useTranslation("loans");
  const client = useApiClient();
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAct = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);
  const canSeeServices = hasView(user, "SERVICE_AGREEMENTS");
  const queryClient = useQueryClient();

  const agreement = useQuery(loanAgreementQuery(client, id));
  const linkedServices = useQuery({
    ...serviceAgreementsQuery(client, { page: 1, loanAgreementId: id }),
    enabled: canSeeServices && id.length > 0,
  });
  const [signing, setSigning] = useState(false);
  const [returning, setReturning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [drawingService, setDrawingService] = useState(false);
  const [confirming, setConfirming] = useState<"issue" | "close" | "cancel" | "renew" | null>(null);
  const [pdfPending, setPdfPending] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // One handler for all three transitions: refresh the cached agreement and
  // drop the confirmation. Leaving the dialog open on success would invite a
  // second click on a transition the server has already refused once.
  const settled = async () => {
    setConfirming(null);
    await queryClient.invalidateQueries({ queryKey: ["loan-agreements"] });
  };

  const issue = useMutation({ ...issueLoanAgreementMutation(client), onSuccess: settled });
  const close = useMutation({ ...closeLoanAgreementMutation(client), onSuccess: settled });
  const cancel = useMutation({ ...cancelLoanAgreementMutation(client), onSuccess: settled });
  const renew = useMutation({
    ...renewLoanAgreementMutation(client),
    onSuccess: async (draft) => {
      setConfirming(null);
      await queryClient.invalidateQueries({ queryKey: ["loan-agreements"] });
      await navigate(`/loan-agreements/${draft.id}`);
    },
  });

  const data = agreement.data;
  const failure = issue.error ?? close.error ?? cancel.error ?? renew.error;
  const canRenew =
    canAct &&
    data !== undefined &&
    (data.status === "ISSUED" || data.status === "SIGNED" || data.status === "CLOSED");
  const canDrawService =
    canAct &&
    canSeeServices &&
    data !== undefined &&
    data.siteId !== null &&
    (data.status === "ISSUED" || data.status === "SIGNED");
  const servicePrefill: ServiceAgreementPrefill | undefined =
    canDrawService && data.siteId
      ? {
          organizationId: data.organizationId,
          siteId: data.siteId,
          siteName: data.siteName,
          loanAgreementId: data.id,
          loanAgreementNumber: data.number,
          customerKind: data.borrowerKind,
          customerName: data.borrowerName,
          customerTaxId: data.borrowerTaxId,
          customerAddress: data.borrowerAddress,
          signatoryName: data.signatoryName,
          signatoryDocument: data.signatoryDocument,
          signatoryCapacity: data.signatoryCapacity,
        }
      : undefined;

  const downloadPdf = async () => {
    setPdfError(null);
    setPdfPending(true);

    try {
      const { blob, filename } = await client.getBlob(`/loan-agreements/${id}/pdf`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename ?? `comodato-${data?.number ?? id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setPdfError(error instanceof Error ? error.message : t("pdfDownloadFailed"));
    } finally {
      setPdfPending(false);
    }
  };

  return (
    <>
      <div className="print:hidden">
        <div className="mb-5">
          <Link
            to="/contracts/loans"
            className="text-sm text-ink-muted underline underline-offset-2"
          >
            {t("backToList")}
          </Link>
          <h1 className="mt-2 text-lg font-semibold tracking-tight text-ink">
            {data ? data.number : t("detailFallbackTitle")}
          </h1>
          <p className="mt-0.5 text-sm text-ink-muted">{t("detailDescription")}</p>
        </div>

        {data ? (
          <Card className="mb-4">
            <CardBody>
              {failure ? <Alert className="mb-3">{failure.message}</Alert> : null}
              {pdfError ? <Alert className="mb-3">{pdfError}</Alert> : null}

              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={LOAN_STATUS_TONES[data.status]}>
                  {LOAN_STATUS_LABELS[data.status]}
                </Badge>

                {data.status === "DRAFT" && canAct ? (
                  <>
                    <Button onClick={() => setConfirming("issue")}>{t("issue")}</Button>
                    <Button variant="secondary" onClick={() => setEditing(true)}>
                      {t("editDraft")}
                    </Button>
                  </>
                ) : null}

                {data.status !== "DRAFT" ? (
                  <>
                    <Button variant="secondary" onClick={() => void downloadPdf()} loading={pdfPending}>
                      {t("downloadPdf")}
                    </Button>
                    <Button variant="secondary" onClick={() => window.print()}>
                      {t("printFileCopy")}
                    </Button>
                  </>
                ) : null}

                {data.status === "ISSUED" && canAct ? (
                  <Button variant="secondary" onClick={() => setSigning(true)}>
                    {t("recordSignature")}
                  </Button>
                ) : null}

                {(data.status === "ISSUED" || data.status === "SIGNED") && canAct ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => setReturning(true)}
                      disabled={outstandingItems(data.items) === 0}
                    >
                      {t("recordReturn")}
                    </Button>
                    <Button variant="secondary" onClick={() => setConfirming("close")}>
                      {t("close")}
                    </Button>
                  </>
                ) : null}

                {(data.status === "DRAFT" || data.status === "ISSUED") && canAct ? (
                  <Button variant="secondary" onClick={() => setConfirming("cancel")}>
                    {t("cancel")}
                  </Button>
                ) : null}

                {canRenew ? (
                  <Button variant="secondary" onClick={() => setConfirming("renew")}>
                    {t("renew")}
                  </Button>
                ) : null}

                {canDrawService ? (
                  <Button variant="secondary" onClick={() => setDrawingService(true)}>
                    {t("drawService")}
                  </Button>
                ) : null}
              </div>

              {data.status !== "DRAFT" ? (
                <Alert tone="info" title={t("printHintTitle")} announce={false} className="mt-3">
                  {t("printHintBody")}
                </Alert>
              ) : null}

              {data.renewedFromId ? (
                <Alert tone="info" title={t("renewalDraftTitle")} announce={false} className="mt-3">
                  {t("renewalDraftBody")}
                </Alert>
              ) : null}

              {data.status === "DRAFT" ? (
                <Alert tone="info" title={t("notPrintableTitle")} announce={false} className="mt-3">
                  {t("notPrintableBody")}
                </Alert>
              ) : null}

              {data.status === "SIGNED" && data.attestedByEmail && data.attestedAt && data.signedOn ? (
                <Alert tone="info" title={t("signedTitle")} announce={false} className="mt-3">
                  {t("signedBody", {
                    email: data.attestedByEmail,
                    attestedAt: formatDateTime(data.attestedAt),
                    signedOn: formatDate(data.signedOn),
                  })}
                </Alert>
              ) : null}
            </CardBody>
          </Card>
        ) : null}
      </div>

      {agreement.isPending ? <LoadingBlock label={t("loadingDetail")} /> : null}
      {agreement.isError ? (
        <ErrorBlock error={agreement.error} onRetry={() => void agreement.refetch()} />
      ) : null}

      {canSeeServices && data ? (
        <Card className="mb-4 print:hidden">
          <CardBody>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-ink">{t("linkedServicesTitle")}</h2>
              {canDrawService ? (
                <Button size="sm" variant="secondary" onClick={() => setDrawingService(true)}>
                  {t("drawService")}
                </Button>
              ) : null}
            </div>
            {linkedServices.isPending ? (
              <p className="text-sm text-ink-muted">{t("linkedServicesLoading")}</p>
            ) : null}
            {linkedServices.isError ? (
              <ErrorBlock
                error={linkedServices.error}
                onRetry={() => void linkedServices.refetch()}
              />
            ) : null}
            {linkedServices.data ? (
              linkedServices.data.data.length === 0 ? (
                <p className="text-sm text-ink-muted">{t("linkedServicesEmpty")}</p>
              ) : (
                <ul className="space-y-2">
                  {linkedServices.data.data.map((service) => (
                    <li
                      key={service.id}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
                    >
                      <Link
                        to={`/service-agreements/${service.id}`}
                        className="font-medium text-brand underline underline-offset-2"
                      >
                        {service.number}
                      </Link>
                      <Badge tone={SERVICE_STATUS_TONES[service.status]}>
                        {SERVICE_STATUS_LABELS[service.status]}
                      </Badge>
                      <span className="text-ink-muted">
                        {service.currency} {service.monthlyAbonoUsd.toFixed(2)}
                        {service.endsOn ? ` · ${formatDate(service.endsOn)}` : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {data ? <AgreementDocument agreement={data} /> : null}

      {data && signing ? (
        <AttestSignatureDialog agreement={data} onClose={() => setSigning(false)} />
      ) : null}

      {data && returning ? (
        <RecordReturnDialog agreement={data} onClose={() => setReturning(false)} />
      ) : null}

      {data && editing ? (
        <LoanAgreementCreateDialog
          open
          agreement={data}
          onClose={() => setEditing(false)}
        />
      ) : null}

      {drawingService && servicePrefill ? (
        <ServiceAgreementCreateDialog
          open
          prefill={servicePrefill}
          onClose={() => setDrawingService(false)}
        />
      ) : null}

      <ConfirmDialog
        open={confirming === "issue"}
        title={t("issueConfirmTitle")}
        confirmLabel={t("issue")}
        pending={issue.isPending}
        onConfirm={() => issue.mutate({ id })}
        onCancel={() => setConfirming(null)}
      >
        {t("issueConfirmBody")}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirming === "close"}
        title={t("closeConfirmTitle")}
        confirmLabel={t("close")}
        pending={close.isPending}
        onConfirm={() => close.mutate({ id })}
        onCancel={() => setConfirming(null)}
      >
        {t("closeConfirmBody")}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirming === "cancel"}
        title={t("cancelConfirmTitle")}
        confirmLabel={t("cancelConfirmLabel")}
        destructive
        pending={cancel.isPending}
        onConfirm={() => cancel.mutate({ id })}
        onCancel={() => setConfirming(null)}
      >
        {t("cancelConfirmBody")}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirming === "renew"}
        title={t("renewConfirmTitle")}
        confirmLabel={t("renewConfirmLabel")}
        pending={renew.isPending}
        onConfirm={() => renew.mutate({ id })}
        onCancel={() => setConfirming(null)}
      >
        {t("renewConfirmBody")}
      </ConfirmDialog>
    </>
  );
}

/**
 * The instrument.
 *
 * Reads back the clause text the server froze at issue, rather than
 * re-rendering a template in the browser. That is the whole point of storing
 * it: the page cannot print anything other than what was agreed.
 */
function AgreementDocument({ agreement }: { agreement: LoanAgreement }) {
  const { t } = useTranslation("loans");
  const settled = agreement.items.filter((item) => item.outcome !== null).length;
  const signedOn = agreement.signedOn;
  const attestedAt = agreement.attestedAt;
  const attestedByEmail = agreement.attestedByEmail;
  const attested = signedOn !== null && attestedAt !== null && attestedByEmail !== null;

  return (
    <article className="loan-agreement-document space-y-6 text-sm text-ink">
      {agreement.draftNotice ? (
        <p className="break-inside-avoid rounded-lg border border-danger bg-danger-soft p-3 text-xs leading-relaxed font-medium whitespace-pre-line text-danger">
          {agreement.draftNotice}
        </p>
      ) : null}

      <header className="border-b border-border-subtle pb-4">
        <h2 className="text-base font-semibold">
          Contrato de comodato de equipamiento — {agreement.number}
        </h2>
        <dl className="mt-3 grid gap-x-6 gap-y-1 sm:grid-cols-2">
          <Entry term={t("metaOrganization")} value={agreement.organizationName} />
          <Entry
            term={t("metaSite")}
            value={`${agreement.siteName}, ${agreement.siteAddress}`}
          />
          <Entry term={t("metaBorrower")} value={agreement.borrowerName} />
          <Entry
            term={t("metaBorrowerIs")}
            value={LEGAL_PERSON_LABELS[agreement.borrowerKind]}
          />
          <Entry
            term={t("metaTerm")}
            value={t("metaTermValue", {
              months: agreement.termMonths,
              starts: formatDate(agreement.startsOn),
              ends: formatDate(agreement.endsOn),
            })}
          />
          <Entry
            term={t("metaStatus")}
            value={LOAN_STATUS_LABELS[agreement.status]}
            className="print:hidden"
          />
          <Entry
            term={t("metaIssued")}
            value={agreement.issuedAt ? formatDateTime(agreement.issuedAt) : t("metaNotIssued")}
            className="print:hidden"
          />
          <Entry
            term={t("metaSignedOnPaper")}
            value={agreement.signedOn ? formatDate(agreement.signedOn) : t("metaNotRecorded")}
          />
        </dl>
      </header>

      {agreement.clauses.length === 0 ? (
        <Statement>{t("noClausesYet")}</Statement>
      ) : (
        <section className="space-y-4">
          {agreement.clauses.map((clause) => (
            <div key={clause.title} className="break-inside-avoid">
              <h3 className="mb-1 text-sm font-semibold text-ink">{clause.title}</h3>
              {clause.body.split("\n\n").map((paragraph, index) => (
                <p key={index} className="mb-2 leading-relaxed whitespace-pre-line text-ink">
                  {paragraph}
                </p>
              ))}
              {clause.titleEn ?? clause.bodyEn ? (
                <div className="mt-3 rounded-md border border-border-subtle bg-surface-muted p-3 text-ink-muted">
                  {clause.titleEn ? (
                    <h4 className="mb-1 text-sm font-medium">{clause.titleEn}</h4>
                  ) : null}
                  {clause.bodyEn
                    ? clause.bodyEn.split("\n\n").map((paragraph, index) => (
                        <p key={index} className="mb-2 text-xs leading-relaxed whitespace-pre-line">
                          {paragraph}
                        </p>
                      ))
                    : null}
                </div>
              ) : null}
            </div>
          ))}
        </section>
      )}

      {agreement.clauses.length > 0 ? <Signatures agreement={agreement} /> : null}

      {attested ? (
        <section className="break-inside-avoid border-t border-border-subtle pt-4">
          <h3 className="mb-2 text-sm font-semibold text-ink">
            Constancia de registro (no es firma digital)
          </h3>
          <p className="leading-relaxed text-ink">
            Fecha en el papel: {formatDate(signedOn)}. Registrada en el sistema el{" "}
            {formatDateTime(attestedAt)} por {attestedByEmail}. Esta constancia acredita que un
            miembro del personal vio el ejemplar firmado; no es firma digital ni electrónica bajo
            la Ley 25.506.
          </p>
        </section>
      ) : null}

      {agreement.fileCopyNotice ? (
        <p className="break-inside-avoid rounded-lg border border-border-subtle bg-surface-muted p-3 text-xs leading-relaxed whitespace-pre-line text-ink-muted">
          {agreement.fileCopyNotice}
        </p>
      ) : null}

      <section className="break-inside-avoid print:hidden">
        <h3 className="mb-2 text-sm font-semibold text-ink">
          {t("equipmentReturns", { settled, total: agreement.items.length })}
        </h3>

        <DataTable caption={t("equipmentReturns", { settled, total: agreement.items.length })}>
          <thead>
            <tr>
              <Th>{t("colDevEui")}</Th>
              <Th>{t("colInstrument")}</Th>
              <Th>{t("colReturned")}</Th>
              <Th>{t("colOutcome")}</Th>
              <Th>{t("colNote")}</Th>
            </tr>
          </thead>

          <tbody>
            {agreement.items.length === 0 ? (
              <EmptyRow colSpan={5}>{t("nothingOnAgreement")}</EmptyRow>
            ) : (
              agreement.items.map((item) => (
                <tr key={item.id}>
                  <Td className="font-mono text-xs">{formatDevEui(item.devEui)}</Td>
                  <Td>
                    {item.manufacturer} {item.model}
                  </Td>
                  <Td>{item.returnedOn ? formatDate(item.returnedOn) : EM_DASH}</Td>
                  <Td>{item.outcome ? LOAN_OUTCOME_LABELS[item.outcome] : t("stillOut")}</Td>
                  <Td>{item.outcomeNotes ?? EM_DASH}</Td>
                </tr>
              ))
            )}
          </tbody>
        </DataTable>
      </section>

      {agreement.notes ? (
        <section className="break-inside-avoid print:hidden">
          <h3 className="mb-2 text-sm font-semibold text-ink">{t("internalNotes")}</h3>
          <p className="whitespace-pre-line text-ink-muted">{agreement.notes}</p>
        </section>
      ) : null}
    </article>
  );
}

/**
 * The signature blocks.
 *
 * Two copies is written on the page because that is what article 1536(e)
 * eventually turns on: the borrower who holds no copy of the terms is the
 * borrower who disputes them.
 */
function Signatures({ agreement }: { agreement: LoanAgreement }) {
  return (
    <section className="break-inside-avoid border-t border-border-subtle pt-4">
      <p className="mb-6 text-ink">
        En prueba de conformidad se firman dos ejemplares de un mismo tenor y a un solo efecto, uno
        para cada parte.
      </p>

      <div className="grid gap-10 sm:grid-cols-2">
        <SignatureBlock
          party="Por el Comodante"
          name={agreement.lenderName}
          detail={`CUIT ${agreement.lenderTaxId}`}
        />
        <SignatureBlock
          party="Por el Comodatario"
          name={agreement.signatoryName}
          detail={`${agreement.signatoryDocument} — ${agreement.signatoryCapacity}`}
        />
      </div>
    </section>
  );
}

function SignatureBlock({ party, name, detail }: { party: string; name: string; detail: string }) {
  return (
    <div>
      <div className="h-12 border-b border-ink" />
      <p className="mt-1 text-xs text-ink-muted">{party}</p>
      <p className="text-sm font-medium text-ink">{name}</p>
      <p className="text-xs text-ink-muted">{detail}</p>
      <p className="mt-2 text-xs text-ink-muted">Aclaración y fecha:</p>
    </div>
  );
}

function Entry({
  term,
  value,
  className,
}: {
  term: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={className ? `flex gap-2 ${className}` : "flex gap-2"}>
      <dt className="text-ink-muted">{term}:</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function Statement({ children }: { children: React.ReactNode }) {
  return (
    <p className="break-inside-avoid rounded-lg border border-border-subtle bg-surface-muted p-3 text-xs leading-relaxed text-ink-muted">
      {children}
    </p>
  );
}
